import { AppError, CommonErrorsKey, approvalAbi, contractBook, isSupportedChainId } from '@eulerxyz/core-sdk'

import type { Config } from '@wagmi/core'
import {
  type Address,
  type Hex,
  type StateMapping,
  type StateOverride,
  encodeAbiParameters,
  encodePacked,
  hexToBigInt,
  keccak256,
  maxUint256,
  publicActions,
  toHex,
  zeroAddress,
} from 'viem'
import { encodeFunctionData } from 'viem/utils'
import { type DebugActions, debugActions } from '../actions/debugActions'
import {
  type TransactionItems,
  isCompoundTransaction,
  isNativeAsset,
  isTxThatNeedsApproval,
  isSwapMulticallTransactionItem,
  isSkimInTransactionItem,
  isDepositTransactionItem,
  isRepayTransactionItem,
} from '../factories'
import { requiresApprovalReset } from './requiresApprovalReset'

export type PermitOverrideParams = {
  chainId?: number
  transactions: TransactionItems
  address: Address
}

export function computeStateOverride(
  transactions: TransactionItems,
  address: Address,
  stateMapping: StateMapping = [],
  chainId?: number,
) {
  return transactions.reduce<StateMapping>((acc, transaction) => {
    // Handle deposit/repay - Permit2 spender is the vault (SkimIn excluded — handled separately)
    if (isTxThatNeedsApproval(transaction) && !isSkimInTransactionItem(transaction)) {
      handleStateMapping(address, transaction.asset.address, transaction.vault, acc)
    }

    // Handle wallet swaps (fromVault === zeroAddress).
    // The swapper calls Permit2.transferFrom with msg.sender=swapper, so
    // the Permit2 allowance check is allowance[user][token][swapper].
    // @ts-ignore - Type narrowing issue
    if (isSwapMulticallTransactionItem(transaction) && transaction.fromVault === zeroAddress) {
      handleStateMapping(address, transaction.assetIn.address, transaction.swapperAddress, acc)
    }

    // Handle SkimIn - simulation override only.
    // WARNING: SkimIn uses Permit2.transferFrom via EVC batch (msg.sender=EVC), which is
    // blocked by assertNoPermit2TransferFrom at execution time. This override exists only
    // for simulation. If SkimIn is needed in production, it must be redesigned to avoid
    // making EVC the Permit2 spender.
    if (isSkimInTransactionItem(transaction) && chainId) {
      const evcAddress = contractBook.evc.address[chainId as keyof typeof contractBook.evc.address]
      if (evcAddress && !isNativeAsset(transaction.asset) && transaction.owner) {
        handleStateMapping(transaction.owner, transaction.asset.address, evcAddress as Address, acc)
      }
    }

    if (isCompoundTransaction(transaction)) {
      computeStateOverride(transaction.transactions, address, acc, chainId)
    }

    return acc
  }, stateMapping)
}

function handleStateMapping(address: Address, asset: Address, spender: Address, acc: StateMapping) {
  // Compute Permit2 allowance slot for mapping: allowance[owner][token][spender]
  // Storage layout: mapping at slot 1
  const baseSlot = keccak256(encodePacked(['uint256', 'uint256'], [hexToBigInt(address), 1n]))
  const assetSlot = keccak256(encodePacked(['uint256', 'uint256'], [hexToBigInt(asset), hexToBigInt(baseSlot)]))
  const spenderSlot = keccak256(encodePacked(['uint256', 'uint256'], [hexToBigInt(spender), hexToBigInt(assetSlot)]))

  if (!acc.find((item) => item.slot === spenderSlot)) {
    // Use maxUint256 to set max allowance with max expiration
    // PackedAllowance struct: { uint160 amount, uint48 expiration, uint48 nonce }
    // maxUint256 sets all fields to max, which works for simulation
    acc.push({ slot: spenderSlot, value: toHex(maxUint256) })
  }
}

/**
 * Compute standard ERC20 approval slot for allowances[owner][spender]
 * Standard ERC20 (OpenZeppelin) has the allowances mapping at slot 1 or 2.
 * The slot is: keccak256(abi.encode(spender, keccak256(abi.encode(owner, mappingSlot))))
 */
function computeErc20ApprovalSlot(owner: Address, spender: Address, mappingSlot: bigint): Hex {
  // Inner slot: keccak256(abi.encode(owner, mappingSlot))
  const innerSlot = keccak256(encodeAbiParameters([{ type: 'address' }, { type: 'uint256' }], [owner, mappingSlot]))
  // Outer slot: keccak256(abi.encode(spender, innerSlot))
  const outerSlot = keccak256(encodeAbiParameters([{ type: 'address' }, { type: 'bytes32' }], [spender, innerSlot]))
  return outerSlot
}

/**
 * OZ 5.0 ERC7201 namespaced storage location for ERC20._allowances.
 * Derived from spec: keccak256(abi.encode(uint256(keccak256("openzeppelin.storage.ERC20")) - 1)) & ~bytes32(uint256(0xff))
 * _allowances is at offset +1 in the ERC20Storage struct (after _balances).
 * See: https://eips.ethereum.org/EIPS/eip-7201
 */
const OZ_ERC20_ALLOWANCES_SLOT = (() => {
  // ERC-7201: keccak256(abi.encode(uint256(keccak256(bytes(id))) - 1)) & ~bytes32(uint256(0xff))
  // Step 1: keccak256(bytes("openzeppelin.storage.ERC20")) — raw UTF-8 bytes, NOT ABI-encoded string
  const namespaceHash = hexToBigInt(keccak256(toHex('openzeppelin.storage.ERC20')))
  // Step 2-3: keccak256(abi.encode(uint256(hash - 1))) & ~0xff
  const storageLocation =
    hexToBigInt(keccak256(encodeAbiParameters([{ type: 'uint256' }], [namespaceHash - 1n]))) & ~0xffn
  return storageLocation + 1n // +1 for _allowances (after _balances in ERC20Storage struct)
})()

async function traceApproval(
  tracer: DebugActions['prestateTracer'],
  to: Address,
  from: Address,
  permit2Address: Address,
  amount = 0n,
): Promise<StateOverride> {
  // Primary: use prestateTracer (diffMode) to discover the actual approval storage slots.
  // We use only `post` state — in diffMode, `post` contains only slots whose final value
  // differs from their initial value. This excludes side-effect slots (reentrancy guards,
  // paused flags, nonces) that are touched during approve() but reset to original values.
  // `pre` includes ALL read slots and would introduce unsafe overrides.
  try {
    const data = encodeFunctionData({
      functionName: 'approve',
      args: [permit2Address, amount],
      abi: approvalAbi,
    })
    const { post } = await tracer({
      data,
      batch: false,
      to,
      from,
    })

    const { storage } = post[to.toLowerCase()] || post[to] || {}
    if (storage) {
      const stateDiff = Object.entries(storage).map(([slot, value]) => ({
        slot: slot as Hex,
        value: amount !== 0n ? value : toHex(maxUint256),
      }))
      return [{ address: to, stateDiff }]
    }
  } catch {
    // Tracer failed (e.g. RPC doesn't support debug_traceCall) - fall through to computed slots
  }

  // Fallback: use brute-force computed slots for common ERC20 storage layouts.
  // This covers cases where the RPC provider doesn't support debug_traceCall.
  const computedSlots = [
    computeErc20ApprovalSlot(from, permit2Address, 0n), // Allowances at slot 0
    computeErc20ApprovalSlot(from, permit2Address, 1n), // OZ < 5.0 standard slot
    computeErc20ApprovalSlot(from, permit2Address, 2n), // Alternative slot
    computeErc20ApprovalSlot(from, permit2Address, 3n), // OZ >= 5.0 slot
    computeErc20ApprovalSlot(from, permit2Address, OZ_ERC20_ALLOWANCES_SLOT), // OZ 5.0 ERC7201 namespaced storage
  ]
  const stateDiff = computedSlots.map((slot) => ({
    slot,
    value: toHex(maxUint256),
  }))
  return [{ address: to, stateDiff }]
}

function getDebugClient(config: Config, chainId: number | undefined) {
  return config.getClient({ chainId }).extend(publicActions).extend(debugActions)
}

export async function permitOverride(
  config: Config,
  { address, transactions, chainId }: PermitOverrideParams,
): Promise<StateOverride> {
  const client = getDebugClient(config, chainId)

  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    throw new AppError(CommonErrorsKey.ChainNotSupported, 'No chain found')
  }

  const permit2Address = contractBook.permit2.address[client.chain.id]

  const permit2StateDiff = computeStateOverride(transactions, address, [], client.chain.id)

  const stateOverride: StateOverride = [
    {
      address: permit2Address,
      stateDiff: permit2StateDiff,
    },
  ]

  const flattenedTxs = transactions.flatMap((tx) => (isCompoundTransaction(tx) ? tx.transactions : [tx]))

  // Separate SkimIn from other transactions because SkimIn uses transaction.owner as the token owner
  const depositRepayTxs = flattenedTxs
    .filter((tx) => isDepositTransactionItem(tx) || isRepayTransactionItem(tx))
    .filter((tx) => !isNativeAsset(tx.asset))
    .filter((tx) => !!tx.asset.address)
    .filter((tx, index, self) => index === self.findIndex((t) => t.asset.address === tx.asset.address))

  const skimInTxs = flattenedTxs
    .filter((tx) => isSkimInTransactionItem(tx))
    .filter((tx) => !isNativeAsset(tx.asset))
    .filter((tx) => !!tx.asset.address)
    .filter((tx) => !!tx.owner) // SkimIn must have an owner

  // Trace approvals for deposit/repay using the wallet address
  const stateOverrideForApprovals = await traceApprovalForStateOverried(
    client,
    address || zeroAddress,
    depositRepayTxs.map((tx) => tx.asset.address),
  )
  stateOverride.push(...stateOverrideForApprovals)

  // Trace approvals for SkimIn using the transaction.owner (not the wallet address)
  // Group SkimIn by owner to batch the traces
  const skimInByOwner = new Map<Address, Address[]>()
  for (const tx of skimInTxs) {
    if (!tx.owner || !tx.asset.address) continue
    const assets = skimInByOwner.get(tx.owner) || []
    if (!assets.includes(tx.asset.address)) {
      assets.push(tx.asset.address)
    }
    skimInByOwner.set(tx.owner, assets)
  }

  for (const [owner, assets] of skimInByOwner) {
    const skimInApprovals = await traceApprovalForStateOverried(client, owner, assets)
    stateOverride.push(...skimInApprovals)
  }

  return stateOverride
}

/**
 * Trace the approval for the given assets
 * @param client - The client
 * @param address - The address
 * @param assets - The assets
 * @returns The state override
 */
async function traceApprovalForStateOverried(
  client: ReturnType<typeof getDebugClient>,
  address: Address,
  assets: Address[],
) {
  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    throw new AppError(CommonErrorsKey.ChainNotSupported, 'No chain found')
  }

  const permit2Address = contractBook.permit2.address[client.chain.id]

  const allowances = await client.readContract({
    abi: contractBook.utilsLens.abi,
    address: contractBook.utilsLens.address[client.chain.id],
    functionName: 'tokenAllowances',
    args: [permit2Address, address, assets],
  })

  const stateOverride: StateOverride = []

  for (const [index, asset] of assets.entries()) {
    const allowance = allowances[index]
    if (typeof allowance === 'undefined') continue
    const resetApproval = requiresApprovalReset(asset) && allowance > 0n ? 0n : maxUint256
    const trace = await traceApproval(client.prestateTracer, asset, address, permit2Address, resetApproval)
    stateOverride.push(...trace)
  }

  return stateOverride
}

/**
 * Compute the state override for the given approvals
 * @param approvals - The approvals [asset, vault]
 * @param address - The address
 * @param stateMapping - The state mapping
 * @returns The state override
 */
export function computeStateOverrideForApprovals(
  approvals: [Address, Address][],
  address: Address,
  stateMapping: StateMapping = [],
) {
  return approvals.reduce<StateMapping>((acc, approval) => {
    handleStateMapping(address, approval[0], approval[1], acc)

    return acc
  }, stateMapping)
}

/**
 * Get the permit overrides for the given approvals
 * @param config - The config
 * @param address - The address
 * @param approvals - The approvals [asset, vault]
 * @returns The permit overrides
 */
export async function getPermitOverrides(config: Config, address: Address, approvals: [Address, Address][]) {
  const client = config.getClient({ chainId: config.state.chainId }).extend(publicActions).extend(debugActions)

  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    throw new AppError(CommonErrorsKey.ChainNotSupported, 'No chain found')
  }

  const permit2Address = contractBook.permit2.address[client.chain.id]

  const stateOverride: StateOverride = [
    {
      address: permit2Address,
      stateDiff: computeStateOverrideForApprovals(approvals, address),
    },
  ]
  const stateOverrideForApprovals = await traceApprovalForStateOverried(
    client,
    address,
    approvals.map(([asset, _]) => asset),
  )
  stateOverride.push(...stateOverrideForApprovals)

  return stateOverride
}
